import React, { useMemo } from "react";
import styled from "styled-components";
import Theme from "@adsk/alloy-react-theme";
import { useTranslations } from "@adsk/acc-issues-translations";
import { AggregatedActivityCard } from "@adsk/alloy-react-activity-log";
import { displayDate, displayTime } from "@adsk/alloy-react-datetime";
import Tooltip, { OverflowTooltip } from "@adsk/alloy-react-tooltip";
import { AlertWarningIcon } from "@adsk/alloy-react-icon";
import { ActivityLogCardCommonProps } from "../activityLogTypes";
import { activityVerbs } from "@adsk/acc-issues-types";
import { ListRowProps } from "@adsk/alloy-react-activity-log/es/layout/ListRow";

export interface AggregatedActivityCardItem {
  label: React.ReactNode;
  value: React.ReactNode;
}

const StyledCustomActivityListRow = styled(AggregatedActivityCard.ListRow)`
  .IssuesActivityLog__aggregated-card-list-row {
    align-items: baseline;
    display: flex;
    margin-bottom: 8px;
  }

  .activity-value {
    ${Theme.typography.bodyMedium};
    word-break: break-word;
  }

  .activity-label {
    min-width: 116px;
    word-break: break-word;
  }
`;

const StyledFooter = styled.div`
  color: ${Theme.colors.charcoal600};
  display: flex;
  align-items: center;
  ${Theme.typography.caption};
  margin-top: 12px;
`;

const StyledAlertWarningIcon = styled(AlertWarningIcon)`
  color: ${Theme.colors.yellowOrange500};
  height: 16px;
  width: 16px;
  display: flex;
`;

const StyledUsername = styled.div`
  display: flex;
  min-width: 0;
  margin-left: 8px;
`;

const StyledTooltip = styled(Tooltip)`
  text-align: center;
  max-width: 310px;
`;

const StyledDateTime = styled.div`
  white-space: pre;
  flex-shrink: 0;
`;

function aggregatedCardRenderRow(props: ListRowProps) {
  return (
    <StyledCustomActivityListRow {...props}>
      {(listRowProps: AggregatedActivityCardItem) => (
        <div className="IssuesActivityLog__aggregated-card-list-row">
          <div className="activity-label">{listRowProps.label}</div>
          <div className="activity-value">{listRowProps.value}</div>
        </div>
      )}
    </StyledCustomActivityListRow>
  );
}

const AutoPlacementIndicationFooter = ({
  date,
  user,
}: {
  date: ActivityLogCardCommonProps["date"];
  user: ActivityLogCardCommonProps["user"];
}) => {
  const { T, locale } = useTranslations();
  const dateAndTimeText = useMemo(() => {
    const dateString = `${T.translate(
      "activityLog.bcfImport.datePrefix"
    )} ${displayDate(date, {
      locale,
      format: "numeric",
      utc: false,
    })}`;

    const timeString = `${T.translate(
      "activityLog.bcfImport.timePrefix"
    )} ${displayTime(date, {
      locale,
      format: "numeric",
      utc: false,
    })}`;

    return ` ${dateString} ${timeString}`;
  }, [T, date, locale]);

  return (
    <StyledFooter>
      <StyledTooltip
        content={T.translate("activityLog.bcfImport.autoPlacementWarning")}
      >
        <StyledAlertWarningIcon />
      </StyledTooltip>
      <StyledUsername>
        <OverflowTooltip data-testid="bcf-activityLog-username-tooltip">
          {user.name}
        </OverflowTooltip>
      </StyledUsername>
      <StyledDateTime>{dateAndTimeText}</StyledDateTime>
    </StyledFooter>
  );
};

export const BcfAggregatedChangesActivityCard = ({
  activityItems,
  isAutoPlacement,
  verb,
  ...props
}: ActivityLogCardCommonProps & {
  activityItems: AggregatedActivityCardItem[];
  verb: string;
  isAutoPlacement: boolean;
}) => {
  const { T } = useTranslations();
  const title = useMemo(
    () =>
      verb === activityVerbs.ISSUE_IMPORT_EDIT
        ? T.translate("activityLog.titles.bcfImport.update")
        : T.translate("activityLog.titles.bcfImport.create"),
    [verb, T]
  );

  return (
    <AggregatedActivityCard
      {...props}
      items={activityItems}
      title={title}
      renderRow={aggregatedCardRenderRow}
      className="IssuesActivityLog__activity-card IssuesActivityLog__activity-card--aggregated"
      {...(isAutoPlacement && {
        renderFooter: () => (
          <AutoPlacementIndicationFooter date={props.date} user={props.user} />
        ),
      })}
    />
  );
};
